// 도감 점수 → 영구 보너스 (PRD 4.2, 계약 1.3). PvE 한정(레이드·넷 판은 호출 쪽이 ZERO를 넘긴다). 순수.
// 값은 소수 비율(0.045 = 4.5%). 계정 보너스 세 출처(도감·연합·펫)가 같은 형식을 쓴다.

/** 쓰기 가능 사본 타입(out 인자용) */
export type Mutable<T> = { -readonly [K in keyof T]: T[K] };

/** deriveStats가 받는 계정 보너스. 치명·받는 피해 감소·HP 재생도 소수 비율 */
export interface AccountBonusIn {
  readonly atkPct: number; readonly hpPct: number; readonly crit: number;
  readonly gold: number; readonly drop: number; readonly xp: number;
  readonly damageTaken: number; readonly hpRegen: number;
}
export const ACCOUNT_BONUS_KEYS: readonly (keyof AccountBonusIn)[] = ['atkPct', 'hpPct', 'crit', 'gold', 'drop', 'xp', 'damageTaken', 'hpRegen'];
export const ZERO_ACCOUNT_BONUS: AccountBonusIn = Object.freeze({ atkPct: 0, hpPct: 0, crit: 0, gold: 0, drop: 0, xp: 0, damageTaken: 0, hpRegen: 0 });
export function makeAccountBonus(): Mutable<AccountBonusIn> { return { atkPct: 0, hpPct: 0, crit: 0, gold: 0, drop: 0, xp: 0, damageTaken: 0, hpRegen: 0 }; }

export interface CodexBonusStep { readonly score: number; readonly add: Partial<AccountBonusIn>; readonly label: string }
/** 점수 문턱별 보너스(PRD 4.2 표, 더해지는 값). 831(완성)은 칭호·오라뿐이라 능력치가 없어 표에 넣지 않는다 */
export const CODEX_BONUS_STEPS: readonly CodexBonusStep[] = [
  { score: 50, add: { gold: 0.02 }, label: '골드 +2%' },
  { score: 100, add: { atkPct: 0.01 }, label: '공격 +1%' },
  { score: 200, add: { hpPct: 0.02 }, label: '최대 HP +2%' },
  { score: 300, add: { drop: 0.03 }, label: '드롭률 +3%' },
  { score: 400, add: { atkPct: 0.015 }, label: '공격 +1.5%' },
  { score: 500, add: { xp: 0.03 }, label: 'EXP +3%' },
  { score: 600, add: { hpPct: 0.03, crit: 0.01 }, label: '최대 HP +3%, 치명 +1%p' },
  { score: 700, add: { atkPct: 0.02 }, label: '공격 +2%' },
  { score: 800, add: { gold: 0.03 }, label: '골드 +3%' },
];
/** 완성 보상(능력치 없음): 칭호 「모험의 서」 + 외형 오라 */
export const CODEX_COMPLETE_STEP = { score: 831, label: '칭호 「모험의 서」 + 오라' } as const;

const round6 = (v: number): number => Math.round(v * 1e6) / 1e6;
/** 부팅 때 한 번 편 누적표: CUMULATIVE[i] = 문턱 0..i를 모두 받았을 때의 합 (부동소수 오차를 없애려 6자리로 반올림) */
const CUMULATIVE: readonly AccountBonusIn[] = (() => {
  const acc = makeAccountBonus();
  const list: AccountBonusIn[] = [];
  for (const s of CODEX_BONUS_STEPS) {
    for (const k of ACCOUNT_BONUS_KEYS) { const a = s.add[k]; if (a !== undefined) acc[k] = round6(acc[k] + a); }
    list.push(Object.freeze({ ...acc }));
  }
  return list;
})();

/**
 * 점수 → 누적 보너스. out을 주면 거기에 써서 할당이 없다(프레임 밖에서도 가볍게). out이 없으면 새 객체.
 * 점수가 NaN·음수면 모두 0, 800 이상이면 표 전체의 합.
 */
export function codexBonus(score: number, out?: Mutable<AccountBonusIn>): AccountBonusIn {
  const o = out ?? makeAccountBonus();
  let n = 0;
  if (score >= 0) for (let i = 0; i < CODEX_BONUS_STEPS.length; i++) if (score >= CODEX_BONUS_STEPS[i].score) n = i + 1;
  const src = n > 0 ? CUMULATIVE[n - 1] : ZERO_ACCOUNT_BONUS;
  for (let i = 0; i < ACCOUNT_BONUS_KEYS.length; i++) { const k = ACCOUNT_BONUS_KEYS[i]; o[k] = src[k]; }
  return o;
}

/** 다음 문턱(보너스 표 → 완성 831 → 없음). 이미 완성했으면 null */
export function nextCodexStep(score: number): { score: number; label: string } | null {
  const s = score >= 0 ? score : 0;
  for (const st of CODEX_BONUS_STEPS) if (s < st.score) return { score: st.score, label: st.label };
  return s < CODEX_COMPLETE_STEP.score ? { score: CODEX_COMPLETE_STEP.score, label: CODEX_COMPLETE_STEP.label } : null;
}
